import { useCallback } from 'react';

import {
  useChatUpdateDataMutationMutation,
  useAddChatUsersDataMutationMutation,
} from '../../../generated';

export interface UseUpdateChatBuyerConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UpdateChatBuyerParams {
  chatId: string;
  buyerId: string;
  buyerUserIds: string[];
}

export const useUpdateChatBuyer = (config: UseUpdateChatBuyerConfig = {}) => {
  const { onSuccess, onError } = config;

  const [updateChat, { loading: isUpdating }] =
    useChatUpdateDataMutationMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  const [addUsers, { loading: isAddingUsers }] =
    useAddChatUsersDataMutationMutation({
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

  const updateChatBuyer = useCallback(
    async ({ chatId, buyerId, buyerUserIds }: UpdateChatBuyerParams) => {
      try {
        // First update the chat with the new buyer
        await updateChat({
          variables: {
            id: chatId,
            chat: {
              buyerId,
            },
          },
        });

        // Then add the buyer users to the chat
        if (buyerUserIds.length > 0) {
          await addUsers({
            variables: {
              id: chatId,
              userIds: buyerUserIds,
              first: 30,
              withAllUserFields: true,
            },
          });
        }

        onSuccess?.();
      } catch (error) {
        onError?.(error as Error);
      }
    },
    [updateChat, addUsers, onSuccess, onError]
  );

  return {
    updateChatBuyer,
    isUpdating: isUpdating || isAddingUsers,
  };
};
